// src/AdminPanel/SetProductDiscount.js
import React, { useState, useEffect } from "react";
import { db } from "../firebase/firebase";
import {
    collection,
    onSnapshot,
    query,
    orderBy,
    doc,
    updateDoc,
} from "firebase/firestore";
import toast from "../utils/toast";

function SetProductDiscount() {
    const [products, setProducts] = useState([]);
    const [search, setSearch] = useState("");
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [discountInput, setDiscountInput] = useState("");
    const [saving, setSaving] = useState(false);

    // ── Pagination ───────────────────────────────────────────────
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(5);
    const PAGE_SIZE_OPTIONS = [5, 15, 25, 50, 100, 200, 400, 800, 1200, 1600];

    // ── Real-time product listener ──────────────────────────────
    useEffect(() => {
        const q = query(collection(db, "products"), orderBy("createdAt", "desc"));
        const unsub = onSnapshot(
            q,
            (snap) => {
                const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
                setProducts(list);
            },
            (err) => {
                console.error(err);
                toast.error("Failed to load products");
            }
        );
        return () => unsub();
    }, []);

    // ── Derived lists ──────────────────────────────────────────
    const filtered = products.filter((p) =>
        p.name?.toLowerCase().includes(search.toLowerCase())
    );

    const discountedProducts = products.filter(
        (p) => p.manualDiscount !== undefined && p.manualDiscount !== null && p.manualDiscount !== ""
    );

    // ── Reset page when search or pageSize changes ─────────────────
    useEffect(() => {
        setCurrentPage(1);
    }, [search, pageSize]);

    // ── Pagination logic ──────────────────────────────────────────
    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const paginated = filtered.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
    );

    // ── Select a product from the table ────────────────────────
    const handleSelect = (product) => {
        setSelectedProduct(product);
        setDiscountInput(
            product.manualDiscount !== undefined && product.manualDiscount !== ""
                ? String(product.manualDiscount)
                : ""
        );
    };

    // ── Save discount ───────────────────────────────────────────
    const handleSave = async () => {
        if (!selectedProduct) return;
        const value = parseFloat(discountInput);
        if (isNaN(value) || value < 0 || value > 100) {
            toast.error("Enter a valid discount between 0 and 100");
            return;
        }
        setSaving(true);
        try {
            await updateDoc(doc(db, "products", selectedProduct.id), {
                manualDiscount: value,
            });
            toast.success(`Discount of ${value}% set for "${selectedProduct.name}"`);
            setSelectedProduct(null);
            setDiscountInput("");
            setSearch("");
        } catch (err) {
            console.error(err);
            toast.error("Failed to save discount");
        } finally {
            setSaving(false);
        }
    };

    // ── Clear discount ──────────────────────────────────────────
    const handleClearDiscount = async (product) => {
        try {
            await updateDoc(doc(db, "products", product.id), {
                manualDiscount: null,
            });
            toast.success(`Discount cleared for "${product.name}"`);
        } catch (err) {
            console.error(err);
            toast.error("Failed to clear discount");
        }
    };

    return (
        <div className="upload-page">
            {/* <div className="upload-container">
                <h2>Set Product Discount</h2>

            {/* ── Search & Product Selection Table ── */}
            <div className="spd-section-card">
                <h2 className="spd-section-heading">
                    Select a Product
                </h2>
                <p className="admin-page-subtitle">Apply specific discounts to individual products.</p>

                {/* Search */}
                <div className="stock-search-bar" style={{ marginBottom: 18 }}>
                    <input
                        type="text"
                        className="stock-search-input"
                        placeholder="Search medicine by name..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    {search && (
                        <button
                            className="stock-clear-search"
                            onClick={() => setSearch("")}
                        >
                            ✕
                        </button>
                    )}
                </div>

                {/* Table */}
                <div className="table-scroll-wrapper">
                    <table className="products-table">
                        <thead>
                            <tr>
                                <th>Image</th>
                                <th>Medicine Name</th>
                                <th>Form</th>
                                <th>Brand</th>
                                <th>Price</th>
                                <th>Current Discount</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="no-results">
                                        {search
                                            ? "No medicines match your search."
                                            : "No medicines found."}
                                    </td>
                                </tr>
                            ) : (
                                paginated.map((product) => (
                                    <tr
                                        key={product.id}
                                        className={
                                            selectedProduct?.id === product.id
                                                ? "row-being-edited"
                                                : ""
                                        }
                                    >
                                        <td>
                                            <img
                                                src={product.picture}
                                                alt={product.name}
                                                className="product-thumb"
                                                onError={(e) => {
                                                    e.target.src = "https://via.placeholder.com/42";
                                                }}
                                            />
                                        </td>
                                        <td className="product-name-cell">{product.name}</td>
                                        <td>{product.type || "—"}</td>
                                        <td>{product.manufacturedBy || "—"}</td>
                                        <td>₨{product.price || "—"}</td>
                                        <td>
                                            {product.manualDiscount !== undefined &&
                                                product.manualDiscount !== null &&
                                                product.manualDiscount !== "" ? (
                                                <span className="spd-discount-badge">
                                                    {product.manualDiscount}%
                                                </span>
                                            ) : (
                                                <span style={{ color: "#aaa", fontSize: 12 }}>
                                                    Not set
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            <button
                                                className="spd-select-btn"
                                                onClick={() => handleSelect(product)}
                                                disabled={selectedProduct?.id === product.id}
                                            >
                                                {selectedProduct?.id === product.id
                                                    ? "Selected"
                                                    : "Set Discount"}
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* ── Pagination ── */}
                {filtered.length > 0 && (
                    <div className="pagination-bar">
                        <div className="pagination-info">
                            Showing{" "}
                            {Math.min((currentPage - 1) * pageSize + 1, filtered.length)}–
                            {Math.min(currentPage * pageSize, filtered.length)} of{" "}
                            {filtered.length}
                        </div>
                        <div className="pagination-controls">
                            <button
                                className="page-btn"
                                onClick={() => setCurrentPage(1)}
                                disabled={currentPage === 1}
                            >
                                «
                            </button>
                            <button
                                className="page-btn"
                                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                            >
                                ‹
                            </button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                                .filter(
                                    (p) =>
                                        p === 1 ||
                                        p === totalPages ||
                                        Math.abs(p - currentPage) <= 1
                                )
                                .reduce((acc, p, idx, arr) => {
                                    if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                                    acc.push(p);
                                    return acc;
                                }, [])
                                .map((item, i) =>
                                    item === "..." ? (
                                        <span key={`ellipsis-${i}`} className="page-ellipsis">
                                            …
                                        </span>
                                    ) : (
                                        <button
                                            key={item}
                                            className={`page-btn${currentPage === item ? " active" : ""}`}
                                            onClick={() => setCurrentPage(item)}
                                        >
                                            {item}
                                        </button>
                                    )
                                )}
                            <button
                                className="page-btn"
                                onClick={() =>
                                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                                }
                                disabled={currentPage === totalPages}
                            >
                                ›
                            </button>
                            <button
                                className="page-btn"
                                onClick={() => setCurrentPage(totalPages)}
                                disabled={currentPage === totalPages}
                            >
                                »
                            </button>
                        </div>
                        <div className="pagination-size">
                            <label>Rows per page:</label>
                            <select
                                value={pageSize}
                                onChange={(e) => {
                                    setPageSize(Number(e.target.value));
                                    setCurrentPage(1);
                                }}
                            >
                                {PAGE_SIZE_OPTIONS.map((s) => (
                                    <option key={s} value={s}>
                                        {s}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}
            </div>

            {/* ── Discount Input Panel ── */}
            {selectedProduct && (
                <div className="spd-section-card spd-input-panel">
                    <h2 className="spd-section-heading">
                        <i
                            className="fa-solid fa-percent"
                            style={{ marginRight: 8, color: "var(--red)" }}
                        ></i>
                        Set Discount for &ldquo;{selectedProduct.name}&rdquo;
                    </h2>

                    <div className="spd-input-row">
                        <div className="spd-product-preview">
                            <img
                                src={selectedProduct.picture}
                                alt={selectedProduct.name}
                                className="spd-preview-img"
                                onError={(e) => {
                                    e.target.src = "https://via.placeholder.com/56";
                                }}
                            />
                            <div className="spd-preview-meta">
                                <div className="spd-preview-name">{selectedProduct.name}</div>
                                <div className="spd-preview-sub">
                                    {selectedProduct.type || ""}{" "}
                                    {selectedProduct.manufacturedBy
                                        ? `· ${selectedProduct.manufacturedBy}`
                                        : ""}
                                </div>
                                <div className="spd-preview-price">
                                    ₨{selectedProduct.price || "—"}
                                </div>
                            </div>
                        </div>

                        <div className="spd-discount-field">
                            <label className="spd-label">Discount (%)</label>
                            <div className="spd-input-group">
                                <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.5"
                                    className="spd-number-input"
                                    placeholder="e.g. 15"
                                    value={discountInput}
                                    onChange={(e) => setDiscountInput(e.target.value)}
                                />
                                <span className="spd-percent-sign">%</span>
                            </div>

                            {discountInput !== "" &&
                                !isNaN(parseFloat(discountInput)) &&
                                selectedProduct.price && (
                                    <div className="spd-preview-calc">
                                        Original: ₨{selectedProduct.price} → After discount:{" "}
                                        <strong>
                                            ₨
                                            {(
                                                selectedProduct.price *
                                                (1 - parseFloat(discountInput) / 100)
                                            ).toFixed(2)}
                                        </strong>
                                    </div>
                                )}
                        </div>

                        <div className="spd-actions">
                            <button
                                className="spd-save-btn"
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? "Saving…" : "Apply Discount"}
                            </button>
                            <button
                                className="spd-cancel-btn"
                                onClick={() => {
                                    setSelectedProduct(null);
                                    setDiscountInput("");
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Products with Discounts Applied ── */}
            <div className="spd-section-card">
                <h2 className="spd-section-heading">
                    <i
                        className="fa-solid fa-check-circle"
                        style={{ marginRight: 8, color: "#1a7a40" }}
                    ></i>
                    Products with Manual Discounts
                    <span className="products-count" style={{ marginLeft: 10 }}>
                        {discountedProducts.length}
                    </span>
                </h2>

                {discountedProducts.length === 0 ? (
                    <div className="no-products">
                        No products have a manual discount set yet.
                    </div>
                ) : (
                    <div className="table-scroll-wrapper">
                        <table className="products-table">
                            <thead>
                                <tr>
                                    <th>Image</th>
                                    <th>Medicine Name</th>
                                    <th>Brand</th>
                                    <th>Original Price</th>
                                    <th>Discount</th>
                                    <th>Discounted Price</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {discountedProducts.map((product) => {
                                    const disc = parseFloat(product.manualDiscount);
                                    const discPrice =
                                        product.price && !isNaN(disc)
                                            ? (product.price * (1 - disc / 100)).toFixed(2)
                                            : "—";
                                    return (
                                        <tr key={product.id}>
                                            <td>
                                                <img
                                                    src={product.picture}
                                                    alt={product.name}
                                                    className="product-thumb"
                                                    onError={(e) => {
                                                        e.target.src = "https://via.placeholder.com/42";
                                                    }}
                                                />
                                            </td>
                                            <td className="product-name-cell">{product.name}</td>
                                            <td>{product.manufacturedBy || "—"}</td>
                                            <td>₨{product.price || "—"}</td>
                                            <td>
                                                <span className="spd-discount-badge">{disc}%</span>
                                            </td>
                                            <td>
                                                <strong>₨{discPrice}</strong>
                                            </td>
                                            <td>
                                                <div className="stock-action-btns">
                                                    <button
                                                        className="spd-select-btn"
                                                        onClick={() => handleSelect(product)}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        className="spd-clear-btn"
                                                        onClick={() => handleClearDiscount(product)}
                                                    >
                                                        Clear
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

export default SetProductDiscount;